import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw, Plus, Trash2, Play, AlertTriangle, CheckCircle2, XCircle, Search,
  ChevronLeft, ChevronRight, Server, Ban, Clock, Loader2, Info, Pencil, Download,
} from 'lucide-react';
import {
  getEvalHarnesses, updateEvalHarness, probeEvalHarness,
  getEvalTasks, getEvalModels, getEvalProviders, getEvalEnvironment,
  createEvalRun, getEvalRuns, getEvalRun, cancelEvalRun,
  getEvalArtifacts, getEvalArtifact, downloadEvalArtifact, deleteEvalArtifacts,
} from '../api';
import Select from '../components/Select';

const POLL_MS = 3000;
const STAGES = ['prepare', 'agent', 'patch', 'verify', 'score'];
const STAGE_LABEL = {
  prepare: 'Starting container', agent: 'Agent working', patch: 'Capturing patch',
  verify: 'Running tests', score: 'Scoring', done: 'Done',
};

// A version appears only when it is pinned — then it is a fact someone set,
// not a guess. Unpinned takes the latest build, which nothing can know before a
// run, so the name stands alone and history records what actually executed.
const harnessLabel = (h) => `${h.label || h.name}${h.pinned_version ? ` · ${h.pinned_version}` : ''}`;

// Spelled out, so the choice is not mistaken for a console or gateway sign-in.
const AUTH_LABEL = {
  auto: 'Auto (from provider)',
  api_key: 'API key',
  bedrock: 'AWS Bedrock',
};
const authLabel = (m) => AUTH_LABEL[m] || m;

// Everything this harness can drive. The server has already scoped the list — to
// what the gateway serves, or to what the harness can speak — and the provider
// names a connection rather than a subset to filter to.
const modelsFor = (models, kind) => (kind ? models.fallback?.[kind] || [] : []);

// Supplies its own endpoint and key, so `resolve_spec` overrides the configured
// provider and a choice made here would never reach the run.
const ownsConnection = (h) => Boolean(h?.holds_own_key && h?.configured);

// Bands sized off the corpus so each holds a comparable share of the 113 tasks.
// F2P is how much the task demands; P2P is how much it must avoid breaking.
const F2P_BANDS = [
  { key: 'xs', label: 'Minimal', hint: '≤ 15', max: 15 },
  { key: 'sm', label: 'Small', hint: '16 – 35', min: 16, max: 35 },
  { key: 'md', label: 'Medium', hint: '36 – 60', min: 36, max: 60 },
  { key: 'lg', label: 'Large', hint: '61 – 110', min: 61, max: 110 },
  { key: 'xl', label: 'Very large', hint: '> 110', min: 111 },
];
const P2P_BANDS = [
  { key: 'xs', label: 'Minimal', hint: '≤ 10', max: 10 },
  { key: 'sm', label: 'Small', hint: '11 – 100', min: 11, max: 100 },
  { key: 'md', label: 'Medium', hint: '101 – 500', min: 101, max: 500 },
  { key: 'lg', label: 'Large', hint: '501 – 3k', min: 501, max: 3000 },
  { key: 'xl', label: 'Very large', hint: '> 3k', min: 3001 },
];
const inBand = (bands, key, n) => {
  const b = bands.find((x) => x.key === key);
  if (!b) return true;
  return (b.min === undefined || n >= b.min) && (b.max === undefined || n <= b.max);
};

const LANG_STYLE = {
  go: 'bg-cyan-500/15 text-cyan-300',
  python: 'bg-blue-500/15 text-blue-300',
  typescript: 'bg-indigo-500/15 text-indigo-300',
  javascript: 'bg-yellow-500/15 text-yellow-300',
  rust: 'bg-orange-500/15 text-orange-300',
};

const fmtUsd = (n) => `$${Number(n || 0).toFixed(4)}`;   // measured spend
const fmtUsd2 = (n) => `$${Number(n || 0).toFixed(2)}`;  // budgets, which people type in whole dollars
const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtBytes = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;
const fmtDur = (s) => {
  const t = Math.round(Number(s || 0));
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m ${t % 60}s`;
  return `${Math.floor(t / 3600)}h ${Math.floor((t % 3600) / 60)}m`;
};
// Mongo returns datetimes without their timezone, so the API can emit a bare
// timestamp. JS reads one of those as local time, which showed every run 5½
// hours before it happened. UTC is what the backend stores, so say so.
const parseTime = (s) => (s ? new Date(/(Z|[+-]\d\d:?\d\d)$/.test(s) ? s : `${s}Z`) : null);
const fmtTime = (iso) => parseTime(iso)?.toLocaleString() ?? '—';
const secondsSince = (iso) => {
  const start = parseTime(iso);
  return start ? Math.max(0, (Date.now() - start.getTime()) / 1000) : 0;
};

const STATUS_STYLE = {
  completed: 'bg-green-500/15 text-green-400',
  running: 'bg-orange/15 text-orange',
  queued: 'bg-blue-500/15 text-blue-400',
  failed: 'bg-red-500/15 text-red-400',
  cancelled: 'bg-fg/10 text-fg-muted',
};

function SortHeader({ label, sortKey, sort, onSort, align = 'left', className = '', title }) {
  const active = sort.key === sortKey;
  return (
    <th className={`font-medium ${className}`} title={title}>
      <button type="button" onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-fg transition-colors ${
          align === 'right' ? 'flex-row-reverse w-full justify-start' : ''} ${
          active ? 'text-fg' : ''}`}>
        {label}
        <span className={`text-[10px] ${active ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true">
          {active && sort.dir === 'desc' ? '▼' : '▲'}
        </span>
      </button>
    </th>
  );
}

function StatusPill({ status }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_STYLE[status] || 'bg-fg/10 text-fg-muted'}`}>
      {status}
    </span>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-4">
      <div className="text-fg-muted text-xs">{label}</div>
      <div className="text-fg text-xl font-semibold mt-1">{value}</div>
      {hint && <div className="text-fg-muted text-[11px] mt-0.5">{hint}</div>}
    </div>
  );
}

// ── progress ─────────────────────────────────────────────────────────────────

// Reasons the harness itself broke, mirroring HARNESS_FAULTS in the engine. A
// trial that scored 0 is a measurement; one of these is a defect.
const HARNESS_FAULTS = new Set([
  'setup_failed', 'verifier_setup_failed', 'agent_failed', 'verifier_unavailable', 'unknown_task',
  'trial_crashed',
]);

// Three outcomes, not two. An agent that ran, was scored, and did not fix the bug
// is the benchmark working — calling that "failed" alongside a container crash
// made a healthy run look broken.
function outcomeOf(result) {
  if (!result) return null;
  if (HARNESS_FAULTS.has(result.reward_source)) return 'error';
  return result.resolved ? 'resolved' : 'unresolved';
}

const OUTCOME = {
  resolved: { label: 'resolved', text: 'text-green-400', bar: 'bg-green-500', Icon: CheckCircle2 },
  unresolved: { label: 'unresolved', text: 'text-red-400', bar: 'bg-red-500/70', Icon: XCircle },
  error: { label: 'error', text: 'text-amber-400', bar: 'bg-amber-500', Icon: AlertTriangle },
  cancelled: { label: 'cancelled', text: 'text-fg-muted', bar: 'bg-fg/25', Icon: Ban },
};

function Outcome({ kind, title = '' }) {
  const o = OUTCOME[kind];
  if (!o) return null;
  return (
    <span className={`flex items-center gap-1 text-xs ${o.text}`} title={title}>
      <o.Icon className="w-3.5 h-3.5 shrink-0" />{o.label}
    </span>
  );
}

// Totals for tasks still running. A finished task is folded into run.totals and
// dropped from here, so the two never cover the same task and can be added.
// Without this the cards sit at zero for the whole of a long trial and then jump.
function liveTotals(run) {
  const live = Object.values(run.task_telemetry || {});
  const tokens = {};
  let cost = 0;
  let toolCalls = 0;
  for (const t of live) {
    // A reported figure supersedes the estimate, exactly as it does at the end.
    cost += t.cost_usd || t.cost_estimated_usd || 0;
    toolCalls += t.total_tool_calls || 0;
    for (const [k, v] of Object.entries(t.tokens || {})) tokens[k] = (tokens[k] || 0) + v;
  }
  return { cost, toolCalls, tokens, sources: live.map((t) => t.cost_source) };
}

// How far through STAGES a task is. 'done' counts as complete.
function stageIndex(stage) {
  if (!stage) return -1;
  if (stage === 'done') return STAGES.length;
  return STAGES.indexOf(stage);
}

// `frozen` is a task the run ended without finishing. Which stage it reached is
// the useful part — it says where things broke — so the symbol carries the
// verdict and the text keeps the location.
function StageBar({ stage, outcome, frozen = false, cancelled = false }) {
  const idx = stageIndex(stage);
  const tone = outcome ? OUTCOME[outcome].bar : 'bg-orange';
  const where = STAGE_LABEL[stage] || 'setup';
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5 flex-1 min-w-[120px]">
        {STAGES.map((s, i) => (
          <div key={s} title={STAGE_LABEL[s]}
            className={`h-1.5 flex-1 rounded-sm ${
              i < idx ? tone : i === idx ? `${tone}${frozen ? '' : ' animate-pulse'}` : 'bg-fg/10'}`} />
        ))}
      </div>
      {frozen ? (
        <span className={`text-[11px] w-24 shrink-0 flex items-center gap-1
          ${cancelled ? 'text-fg-muted' : 'text-red-400'}`}
          title={`${cancelled ? 'Cancelled' : 'Failed'} during ${where}`}>
          {cancelled ? <Ban className="w-3 h-3 shrink-0" /> : <XCircle className="w-3 h-3 shrink-0" />}
          <span className="truncate">{where}</span>
        </span>
      ) : (
        <span className="text-[11px] text-fg-muted w-24 shrink-0">
          {idx < 0 ? 'queued' : STAGE_LABEL[stage] || stage}
        </span>
      )}
    </div>
  );
}

// A run is a batch of tasks, each moving through the same five stages. The bar
// answers "how far along" at a glance; expanding answers "where exactly, and did
// anything break" without leaving the page.
function RunProgress({ run, results }) {
  const [open, setOpen] = useState(false);
  // A set, not one id: comparing two lanes side by side is the reason to open
  // them at all, and an accordion closes the one you were reading.
  const [openTasks, setOpenTasks] = useState(() => new Set());
  const [files, setFiles] = useState({});

  const toggleTask = (id) => setOpenTasks((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  // Only when expanded: a history list of 25 runs should not fetch 25 listings.
  const loadFiles = useCallback(async () => {
    try {
      const r = await getEvalArtifacts(run.run_id);
      setFiles(r.data.tasks || {});
    } catch { setFiles({}); }
  }, [run.run_id]);
  // Refetched as tasks land, not only when expanded: a trial writes its patch and
  // verifier logs at the end, so a listing taken mid-run shows the transcript alone
  // and would never catch up.
  useEffect(() => {
    if (open) loadFiles();
  }, [open, loadFiles, run.status, run.completed_tasks]);

  const stages = run.task_stages || {};
  const byTask = Object.fromEntries((results || []).map((r) => [r.task_id, r]));
  // A finished run cannot have a live task. The stored stage is whatever it
  // reached when the run stopped, so a task with no result is stopped, not
  // running — otherwise a failed run keeps animating a stage nothing is working on.
  const over = ['completed', 'failed', 'cancelled'].includes(run.status);
  const cancelled = run.status === 'cancelled';
  const telemetry = run.task_telemetry || {};
  const rows = (run.task_ids || []).map((id) => ({
    id,
    stage: stages[id],
    result: byTask[id],
    stopped: over && !byTask[id],
    live: telemetry[id],
    // A task the run never reached is an error only if the run did not finish
    // cleanly; a cancelled run's leftovers are neither scored nor broken.
    outcome: outcomeOf(byTask[id]) ?? (over && !byTask[id] ? (cancelled ? 'cancelled' : 'error') : null),
  }));
  const done = rows.filter((r) => r.result).length;
  const count = (kind) => rows.filter((r) => r.outcome === kind).length;
  const finished = run.status === 'completed';
  const pct = rows.length ? Math.round((done / rows.length) * 100) : 0;

  return (
    <div className="bg-panel border border-panel-border rounded-xl overflow-hidden">
      <button onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-field/40 transition-colors">
        <ChevronRight className={`w-4 h-4 text-fg-muted shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        <div className="flex-1 min-w-0">
          {/* The run finishing and the tasks passing are different facts. This line
              states the first plainly; the counts beside it carry the second. */}
          <div className="flex items-center gap-2 flex-wrap">
            {finished && <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" />}
            <span className="text-fg-muted text-sm tabular-nums">
              {finished ? `Run complete · ${rows.length} ${rows.length === 1 ? 'task' : 'tasks'}`
                : `${done} of ${rows.length} tasks complete`}
            </span>
            {run.is_active && <Loader2 className="w-3.5 h-3.5 text-orange animate-spin" />}
            {['resolved', 'unresolved', 'error', 'cancelled'].map((kind) => (
              count(kind) > 0 && (
                <span key={kind} className={`text-[11px] ${OUTCOME[kind].text}`}>
                  {count(kind)} {OUTCOME[kind].label}
                </span>
              )
            ))}
          </div>
          <div className="mt-2 h-1.5 flex gap-0.5" role="progressbar" aria-valuenow={pct}
            aria-valuemin={0} aria-valuemax={100}>
            {rows.map(({ id, stage, outcome }) => (
              <div key={id} title={`${id}: ${outcome
                ? OUTCOME[outcome].label + (outcome === 'error' ? ` during ${STAGE_LABEL[stage] || 'setup'}` : '')
                : (STAGE_LABEL[stage] || 'queued')}`}
                className={`flex-1 rounded-sm ${outcome ? OUTCOME[outcome].bar
                  : stage ? 'bg-orange animate-pulse' : 'bg-fg/10'}`} />
            ))}
          </div>
        </div>
        <span className="text-fg-muted text-xs tabular-nums shrink-0">{pct}%</span>
      </button>

      {open && (
        <div className="border-t border-panel-border divide-y divide-panel-border/50">
          {rows.map(({ id, stage, result, stopped, live, outcome }) => {
            // Anything that has started opens: a scored task, or a live one at any
            // stage. Queued has no stage and nothing to say, so it stays shut.
            const expandable = Boolean(result || stage);
            const expanded = openTasks.has(id);
            return (
              <div key={id}>
                <button
                  onClick={() => expandable && toggleTask(id)}
                  title={expandable ? undefined : 'Queued — waiting for a free lane'}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left
                    ${expandable ? 'hover:bg-field/40 cursor-pointer' : 'cursor-default'}`}>
                  <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform
                    ${expandable ? 'text-fg-muted' : 'text-transparent'} ${expanded ? 'rotate-90' : ''}`} />
                  <span className="font-mono text-xs text-fg truncate w-56 shrink-0" title={id}>{id}</span>
                  <div className="flex-1 min-w-0">
                    <StageBar stage={result ? 'done' : stage} outcome={outcome}
                      frozen={stopped} cancelled={cancelled} />
                  </div>
                  {!result && live && (
                    <span className="hidden sm:block text-[10px] text-fg-muted tabular-nums shrink-0"
                      title={`${live.turns || 0} turns · ${live.total_tool_calls || 0} tool calls`}>
                      {fmtInt(live.tokens?.total)} tok · {live.cost_source === 'unpriced'
                        ? '—' : fmtUsd(live.cost_usd || live.cost_estimated_usd)}
                    </span>
                  )}
                  <span className="w-24 shrink-0 flex items-center justify-end text-xs">
                    {outcome
                      ? <Outcome kind={outcome} title={result?.error || ''} />
                      : <span className="text-fg-muted">{stage ? 'running' : 'queued'}</span>}
                  </span>
                </button>

                {expanded && (result
                  ? <TaskResult result={result} runId={run.run_id}
                      files={files[id]} onDeleted={loadFiles} />
                  : <LiveTaskDetail live={live} stage={stage} />)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// The files a task left behind, read on demand. A transcript can be tens of MB,
// so the server returns its tail — which is where a failure shows.
function TaskArtifacts({ runId, taskId, files, onDeleted }) {
  const [open, setOpen] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const names = Object.keys(files || {});
  if (!names.length) return null;

  const view = async (name) => {
    if (open === name) { setOpen(null); return; }
    setBusy(true);
    try {
      const r = await getEvalArtifact(runId, taskId, name);
      setText(r.data.truncated ? `… showing the last ${fmtBytes(r.data.text.length)} of ${fmtBytes(r.data.size)}\n\n${r.data.text}` : r.data.text);
      setOpen(name);
    } catch (e) { setText(e.response?.data?.detail || e.message); setOpen(name); }
    finally { setBusy(false); }
  };

  // Viewing shows a tail; this keeps the whole file. The object URL is revoked
  // straight after — a 455KB blob held for the life of the page is a leak.
  const download = async (name) => {
    setBusy(true);
    try {
      const r = await downloadEvalArtifact(runId, taskId, name);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${runId}_${taskId}_${name.split('/').pop()}`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };

  const remove = async (name) => {
    setBusy(true);
    try {
      await deleteEvalArtifacts(runId, taskId, name);
      if (open === name) setOpen(null);
      onDeleted();
    } finally { setBusy(false); }
  };

  const removeAll = async () => {
    if (!window.confirm(`Delete all ${names.length} files for ${taskId}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await deleteEvalArtifacts(runId, taskId);
      setOpen(null);
      onDeleted();
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-2 border-t border-panel-border/50">
      <div className="flex flex-wrap items-center gap-2">
        {names.map((n) => {
          const base = n.split('/').pop();
          return (
            <span key={n} className={`flex items-center rounded border text-[11px] font-mono
              ${open === n ? 'border-orange text-fg' : 'border-field-border text-fg-muted'}`}>
              <button onClick={() => view(n)} disabled={busy}
                className="px-2 py-0.5 hover:text-fg transition-colors">
                {base} <span className="opacity-60">{fmtBytes(files[n])}</span>
              </button>
              <button onClick={() => download(n)} disabled={busy}
                title={`Download ${base}`} aria-label={`Download ${base}`}
                className="px-1.5 py-0.5 border-l border-field-border hover:text-orange transition-colors">
                <Download className="w-3 h-3" />
              </button>
              <button onClick={() => remove(n)} disabled={busy}
                title={`Delete ${base}`} aria-label={`Delete ${base}`}
                className="px-1.5 py-0.5 border-l border-field-border hover:text-red-400 transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        {/* Named separately from the per-file buttons, and confirmed: it takes the
            patch and verifier logs too, which are the evidence for the score. */}
        <button onClick={removeAll} disabled={busy}
          className="px-2 py-0.5 rounded border border-field-border text-[11px] text-fg-muted hover:text-red-400">
          Delete all {names.length}
        </button>
      </div>
      {open && (
        <pre className="mt-2 max-h-80 overflow-auto bg-field/40 rounded p-2 text-[11px] font-mono text-fg-muted whitespace-pre-wrap break-all">
          {text}
        </pre>
      )}
    </div>
  );
}

// One test group as a filled bar. The fraction is the point — "5/6" and "50/60"
// read the same in text, and only one of them is nearly solved.
function TestBar({ label, passed, total, decisive, hint }) {
  const pct = total ? Math.round((passed / total) * 100) : 0;
  const complete = total > 0 && passed === total;
  return (
    <div className="flex items-center gap-2" title={hint}>
      <span className={`font-mono text-[11px] w-8 shrink-0 ${decisive ? 'text-fg' : 'text-fg-muted'}`}>
        {label}
      </span>
      <div className="h-1.5 w-24 rounded-sm bg-fg/10 overflow-hidden shrink-0">
        <div className={`h-full rounded-sm ${complete ? 'bg-green-500' : 'bg-red-500/70'}`}
          style={{ width: `${pct}%` }} />
      </div>
      <span className={`tabular-nums text-[11px] ${complete ? 'text-green-400' : 'text-red-400'}`}>
        {fmtInt(passed)}/{fmtInt(total)}
      </span>
    </div>
  );
}

// Bars relative to the busiest tool: a read-heavy trial and an edit-heavy one
// describe different behaviour, which a column of numbers hides.
function ToolMix({ counts }) {
  const tools = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);
  if (!tools.length) return null;
  const busiest = tools[0][1];
  return (
    <div className="flex flex-wrap gap-1.5">
      {tools.map(([name, n]) => (
        <span key={name} title={`${name}: ${n} calls`}
          className="relative overflow-hidden rounded border border-field-border
            px-1.5 py-0.5 font-mono text-[10px] text-fg-muted">
          <span className="absolute inset-y-0 left-0 bg-orange/15"
            style={{ width: `${(n / busiest) * 100}%` }} aria-hidden="true" />
          <span className="relative">{name} <span className="text-fg">{n}</span></span>
        </span>
      ))}
    </div>
  );
}

function Metric({ label, value, note, title }) {
  return (
    <div className="min-w-0">
      <div className="text-fg-muted text-[10px] uppercase tracking-wide">{label}</div>
      <div className="text-fg text-sm tabular-nums truncate" title={title}>
        {value}
        {note && <span className="text-orange/80 text-[10px] ml-1">{note}</span>}
      </div>
    </div>
  );
}

// The same breakdown for a task still working. Tests, patch and artifacts are
// absent by definition — the verifier has not run — so this shows what is known
// rather than a row of dashes.
function LiveTaskDetail({ live, stage }) {
  const tok = live?.tokens || {};
  // A task building its container has started but counted nothing yet. Dashes
  // hold the shape so the panel fills in place, rather than the row refusing to
  // open until the agent happens to produce its first event.
  const n = (v) => (live ? fmtInt(v) : '—');
  return (
    <div className="px-4 pb-4 pl-[4.5rem] pt-3 bg-field/20 space-y-3">
      {!live && (
        <div className="text-[11px] text-fg-muted">
          {STAGE_LABEL[stage] || 'Preparing'} — counts begin when the agent does.
        </div>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-6 gap-y-2">
        <Metric label="Cost" note={live?.cost_source === 'estimated' ? 'est.' : ''}
          value={!live || live.cost_source === 'unpriced'
            ? '—' : fmtUsd(live.cost_usd || live.cost_estimated_usd)} />
        <Metric label="Tokens" value={n(tok.total)} />
        {/* Total in, not the uncached remainder — see the Tokens stat above. */}
        <Metric label="In / out"
          value={live ? `${fmtInt(tok.input + tok.cache_read + tok.cache_write)} / ${fmtInt(tok.output)}` : '—'}
          title={live ? `${fmtInt(tok.input)} uncached · ${fmtInt(tok.cache_read)} cached · ${fmtInt(tok.output)} out` : undefined} />
        <Metric label="Cache r / w" value={live ? `${fmtInt(tok.cache_read)} / ${fmtInt(tok.cache_write)}` : '—'}
          title={live ? `${fmtInt(tok.cache_read)} read · ${fmtInt(tok.cache_write)} written` : undefined} />
        <Metric label="Tools" value={n(live?.total_tool_calls)} />
        <Metric label="Turns" value={n(live?.turns)} />
      </div>
      <ToolMix counts={live?.tool_counts} />
    </div>
  );
}

// What a finished task actually produced. Shown on demand: it is the answer to
// "why did this score what it did", which is only interesting for the task asked about.
function TaskResult({ result, runId, files, onDeleted }) {
  const tests = result.tests || {};
  const tok = result.tokens || {};
  const scored = tests.f2p_total !== undefined;

  return (
    <div className="px-4 pb-4 pl-[4.5rem] pt-3 bg-field/20 space-y-3">
      <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
        {scored && (
          <div className="space-y-1.5">
            {/* f2p alone decides resolution; p2p only has to not regress. Same
                weight for both would misstate which number is the verdict. */}
            <TestBar label="f2p" passed={tests.f2p_passed} total={tests.f2p_total} decisive
              hint="Fail-to-pass: must all pass for the task to count as resolved" />
            <TestBar label="p2p" passed={tests.p2p_passed} total={tests.p2p_total}
              hint="Pass-to-pass: already passing, must not regress" />
          </div>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-2 flex-1 min-w-0">
          <Metric label="Cost" value={result.cost_source === 'unpriced' ? '—' : fmtUsd(result.cost_usd)}
            note={result.cost_source === 'estimated' ? 'est.' : ''} />
          <Metric label="Tokens" value={fmtInt(tok.total)}
            title={`${fmtInt(tok.input + tok.cache_read + tok.cache_write)} in `
              + `(${fmtInt(tok.input)} uncached) · ${fmtInt(tok.output)} out`} />
          <Metric label="Cache r / w" value={`${fmtInt(tok.cache_read)} / ${fmtInt(tok.cache_write)}`}
            title={`${fmtInt(tok.cache_read)} read · ${fmtInt(tok.cache_write)} written`} />
          <Metric label="Tools" value={fmtInt(result.total_tool_calls)} />
          <Metric label="Turns" value={fmtInt(result.turns)} />
          <Metric label="Time" value={fmtDur(result.duration_sec)} />
          <Metric label="Patch" value={result.patch_size_bytes ? fmtBytes(result.patch_size_bytes) : '—'} />
        </div>
      </div>

      <ToolMix counts={result.tool_counts} />

      {(result.timed_out || result.budget_stopped) && (
        <div className="flex items-center gap-1.5 text-orange text-[11px]">
          <Clock className="w-3 h-3 shrink-0" />
          {result.timed_out ? 'Stopped at the task timeout.' : 'Stopped at the task budget.'}
        </div>
      )}
      {result.error && (
        <div className="flex items-start gap-1.5 text-red-400 text-[11px]">
          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
          <span className="font-mono break-all">{result.error}</span>
        </div>
      )}
      {runId && <TaskArtifacts runId={runId} taskId={result.task_id} files={files} onDeleted={onDeleted} />}
    </div>
  );
}


// ── Harnesses tab ────────────────────────────────────────────────────────────

// A harness is code (see harness-eval/docs/adding-a-harness.md). Only the
// operator's choices are editable here.
function HarnessRow({ h, providers, suggested, models, effortLevels, defaultEffort, onChanged, onMessage }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const open = () => {
    setForm({
      provider: h.provider || suggested?.[h.kind] || '',
      auth_mode: h.auth_mode || 'auto',
      base_url: h.base_url || '',
      effort: h.effort || defaultEffort || 'medium',
      api_key: '',   // never prefilled; blank on save keeps the stored key
    });
    setEditing(true);
  };

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); onMessage({ type: 'success', text: ok }); onChanged(); return true; }
    catch (e) { onMessage({ type: 'error', text: e.response?.data?.detail || e.message }); return false; }
    finally { setBusy(false); }
  };

  // `api_family`, not `type`: every row is `aisdk` since the cutover, so filtering on
  // `type` left every harness's provider dropdown empty.
  const usable = providers.filter(
    (p) => (h.provider_types || []).includes(p.api_family));

  const save = async () => {
    if (await act(() => updateEvalHarness(h.kind, form), `${h.label} saved`)) setEditing(false);
  };

  return (
    <>
      <tr className="border-b border-panel-border/50">
        <td className="py-2 pr-3">
          <div className="text-fg text-sm">{h.label}</div>
          <div className="text-fg-muted text-[11px]">{h.description}</div>
        </td>
        <td className="py-2 pr-3 text-xs">
          {/* A harness with its own key connects to that gateway, whatever the
              stored provider says — resolve_spec overrides it. Showing the stored
              value read "bedrock" for a harness that runs on the gateway. */}
          {ownsConnection(h)
            ? <span className="text-fg-muted font-mono">gateway</span>
            : h.provider
              ? <span className="text-fg-muted font-mono">{h.provider}</span>
              : <span className="text-yellow-400">not set</span>}
        </td>
        <td className="py-2 pr-3">
          {/* Not gated on `available`: that reports whether the CLI happens to be
              on this machine, which a trial never uses — it installs the harness
              inside each task container. A docker host without the CLI runs
              benchmarks perfectly well. */}
          <input type="checkbox" checked={!!h.enabled} className="accent-orange" disabled={busy}
            onChange={(e) => act(() => updateEvalHarness(h.kind, { enabled: e.target.checked }), 'Saved')} />
        </td>
        <td className="py-2 pl-3 text-right whitespace-nowrap">
          <button disabled={busy} title="Configure" onClick={open}
            className="p-1.5 text-fg-muted hover:text-fg rounded hover:bg-field disabled:opacity-50">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button disabled={busy || !h.available}
            title={h.available ? 'Probe the host copy\u2019s version' : 'No host copy to probe \u2014 trials install their own'}
            onClick={() => act(() => probeEvalHarness(h.kind), 'Version updated')}
            className="p-1.5 text-fg-muted hover:text-fg rounded hover:bg-field disabled:opacity-30">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>
      {editing && (
        <tr className="border-b border-panel-border/50 bg-orange/5">
          <td colSpan={6} className="p-3">
            <div className="flex flex-wrap items-end gap-3">
              {!ownsConnection(h) && (
                <div>
                  <label className="block text-xs text-fg-muted mb-1">Provider</label>
                  <Select className="w-52" placeholder="— pick a provider —" value={form.provider}
                    onChange={(v) => setForm({ ...form, provider: v, model: '' })}
                    options={usable.map((p) => ({
                      value: p.name,
                      label: `${p.name} (${p.type})`,
                      hint: p.configured ? '' : 'no key in Settings',
                    }))} />
                </div>
              )}
              <div>
                <label className="block text-xs text-fg-muted mb-1">Reasoning effort</label>
                <Select className="w-40" value={form.effort}
                  onChange={(v) => setForm({ ...form, effort: v })}
                  options={(effortLevels?.length ? effortLevels : ['low', 'medium', 'high', 'xhigh'])
                    .map((e) => ({ value: e, label: e }))} />
              </div>
              {(h.auth_modes || []).length > 1 && (
                <div>
                  <label className="block text-xs text-fg-muted mb-1">Auth method</label>
                  <Select className="w-56" value={form.auth_mode}
                    onChange={(v) => setForm({ ...form, auth_mode: v })}
                    options={h.auth_modes.map((m) => ({ value: m, label: authLabel(m) }))} />
                </div>
              )}
              {h.kind === 'latentcode' && (
                <>
                  <div>
                    <label className="block text-xs text-fg-muted mb-1">Gateway URL</label>
                    <input value={form.base_url ?? ''} placeholder="https://latentstack.dev/v1"
                      onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                      className="w-64 px-2 py-1 bg-field border border-field-border rounded text-fg text-xs font-mono focus:border-orange" />
                  </div>
                  <div>
                    <label className="block text-xs text-fg-muted mb-1">
                      API key {h.configured && <span className="text-fg-muted">· {h.api_key_masked} saved</span>}
                    </label>
                    <input type="password" value={form.api_key ?? ''} autoComplete="new-password"
                      placeholder={h.configured ? 'leave blank to keep' : 'ls-…'}
                      onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                      className="w-56 px-2 py-1 bg-field border border-field-border rounded text-fg text-xs font-mono focus:border-orange" />
                  </div>
                </>
              )}
              <button onClick={save} disabled={busy}
                className="px-3 py-1.5 bg-orange hover:bg-orange-hover text-fg rounded text-xs disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-fg-muted hover:text-fg text-xs">
                Cancel
              </button>
            </div>
            <p className="text-fg-muted text-[11px] mt-2 font-mono">{h.command || 'not installed'}</p>
          </td>
        </tr>
      )}
    </>
  );
}

function HarnessesTab({ harnesses, providers, suggested, models, effortLevels, defaultEffort, onChanged, onMessage }) {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-6">
      <h2 className="text-fg font-semibold">Harnesses</h2>
      <p className="text-fg-muted text-xs mt-1 mb-3">
        Driving an agent CLI needs an adapter, so this list is fixed by the code — see
        <span className="font-mono"> harness-eval/docs/adding-a-harness.md</span> to add one.
        Choose which provider serves each and which model it runs; endpoints and keys come from
        <strong> Settings → Providers</strong>.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-fg-muted border-b border-panel-border">
              {['Harness', 'Provider', 'On'].map((c) => (
                <th key={c} className="py-2 pr-3 font-medium">{c}</th>
              ))}
              <th className="py-2 pl-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {harnesses.map((h) => (
              <HarnessRow key={h.kind} h={h} providers={providers} suggested={suggested}
                models={models} effortLevels={effortLevels} defaultEffort={defaultEffort}
                onChanged={onChanged} onMessage={onMessage} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── New run tab ──────────────────────────────────────────────────────────────

function NewRunTab({ harnesses, models, tasks, languages, env, onLaunched, onMessage }) {
  const [kind, setKind] = useState('');
  const [model, setModel] = useState('');
  const [taskBudget, setTaskBudget] = useState('5');
  const [runBudget, setRunBudget] = useState('');
  const [lanes, setLanes] = useState('');
  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState('');
  const [f2pBand, setF2pBand] = useState('');
  const [p2pBand, setP2pBand] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const enabled = harnesses.filter((h) => h.enabled);
  useEffect(() => { if (!kind && enabled.length) setKind(enabled[0].kind); }, [enabled, kind]);

  const harness = harnesses.find((h) => h.kind === kind);

  // A model belongs to the harness that can drive it. Kept across a harness
  // change, the picker went on displaying an id the new one cannot speak — the
  // control looked like a valid selection and the launch failed with a 400 from
  // the server naming a model the operator thought they had chosen.
  const offered = modelsFor(models, kind);
  useEffect(() => {
    if (model && offered.length && !offered.includes(model)) setModel('');
  }, [model, offered]);

  const visible = useMemo(() => tasks.filter((t) => {
    if (language && t.language !== language) return false;
    if (f2pBand && !inBand(F2P_BANDS, f2pBand, t.f2p_count || 0)) return false;
    if (p2pBand && !inBand(P2P_BANDS, p2pBand, t.p2p_count || 0)) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return t.task_id.toLowerCase().includes(q) || (t.title || '').toLowerCase().includes(q);
  }), [tasks, query, language, f2pBand, p2pBand]);

  // Three states per column: ascending, descending, then back to the order the
  // corpus ships in. Without the third click there is no way back to the
  // repo's own ordering once you have sorted.
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const sortBy = (key) => setSort((prev) => {
    if (prev.key !== key) return { key, dir: 'asc' };
    if (prev.dir === 'asc') return { key, dir: 'desc' };
    return { key: null, dir: 'asc' };
  });

  const rows = useMemo(() => {
    if (!sort.key) return visible;   // corpus order, as loaded
    const numeric = sort.key === 'f2p_count' || sort.key === 'p2p_count';
    const pick = (t) => (numeric ? (t[sort.key] || 0) : String(t[sort.key] || '').toLowerCase());
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...visible].sort((a, b) => {
      const x = pick(a); const y = pick(b);
      if (x < y) return -1 * sign;
      if (x > y) return 1 * sign;
      return a.task_id.localeCompare(b.task_id);   // stable: many tasks share an F2P count
    });
  }, [visible, sort]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const recommended = env?.recommended_concurrency || 1;
  const maxLanes = env?.max_concurrency || 4;
  useEffect(() => { setLanes(String(recommended)); }, [recommended]);

  const launch = async () => {
    const taskIds = [...selected];
    if (!kind) { onMessage({ type: 'error', text: 'Pick a harness' }); return; }
    // Without this the request omits the model, the harness falls back to whatever
    // it has stored, and the failure surfaces as a preflight error naming a model
    // nobody chose.
    if (!model) { onMessage({ type: 'error', text: 'Pick a model' }); return; }
    if (!taskIds.length) { onMessage({ type: 'error', text: 'Pick at least one task' }); return; }
    const taskUsd = parseFloat(taskBudget);
    if (!(taskUsd > 0)) { onMessage({ type: 'error', text: 'Task budget must be greater than 0' }); return; }
    const runUsd = runBudget === '' ? 0 : parseFloat(runBudget);
    if (!(runUsd >= 0)) { onMessage({ type: 'error', text: 'Run budget cannot be negative' }); return; }
    if (runUsd > 0 && runUsd < taskUsd) {
      onMessage({ type: 'error', text: 'Run budget is below the per-task budget' }); return;
    }

    setBusy(true);
    try {
      const res = await createEvalRun({
        kind, task_ids: taskIds, task_budget_usd: taskUsd, run_budget_usd: runUsd,
        concurrency: Number(lanes) || recommended,
        model: model || undefined,
      });
      onMessage({ type: 'success', text: `Run ${res.data.run.run_id} started` });
      setSelected(new Set());
      onLaunched(res.data.run.run_id);
    } catch (e) {
      onMessage({ type: 'error', text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {(!env.docker_available || !env.corpus_ok) && (
        <div className="p-3 rounded-lg text-sm flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {!env.corpus_ok && <>Task corpus unavailable: {env.corpus_error}. </>}
            {!env.docker_available && (env.docker_cli_present
              ? <>The Docker <strong>daemon is not reachable</strong> on the server — the CLI is installed but not running. Runs are blocked, because a trial could execute and never be scored.</>
              : <>Docker is not installed on the server. Runs are blocked, because a trial could execute and never be scored.</>)}
          </span>
        </div>
      )}

      <div className="bg-panel border border-panel-border rounded-xl p-6">
        <h2 className="text-fg font-semibold mb-4">Configure run</h2>
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs text-fg-muted mb-1">
              Harness <span className="text-orange" aria-hidden="true">*</span>
            </label>
            <Select className="w-64" value={kind} onChange={setKind}
              placeholder={enabled.length === 0 ? 'No harness enabled — configure one first' : 'Pick a harness'}
              options={enabled.map((h) => ({ value: h.kind, label: harnessLabel(h) }))} />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">
              Model <span className="text-orange" aria-hidden="true">*</span>
            </label>
            <Select className="w-72" value={model} onChange={setModel}
              placeholder="— pick a model —"
              options={offered.map((id) => ({ value: id, label: id }))} />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">Task budget (USD)</label>
            <input type="number" step="0.5" min="0" value={taskBudget}
              onChange={(e) => setTaskBudget(e.target.value)}
              className="w-32 px-3 py-1.5 bg-field border border-field-border rounded text-fg text-sm focus:border-orange" />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">Run budget (USD)</label>
            <input type="number" step="1" min="0" value={runBudget} placeholder="no cap"
              onChange={(e) => setRunBudget(e.target.value)}
              className="w-32 px-3 py-1.5 bg-field border border-field-border rounded text-fg text-sm focus:border-orange" />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">
              Tasks at once <span className="text-fg-muted">· {recommended} recommended</span>
            </label>
            <Select className="w-36" value={lanes} onChange={setLanes}
              options={Array.from({ length: maxLanes }, (_, i) => i + 1).map((n) => ({
                value: String(n), label: String(n), hint: n === recommended ? 'recommended' : '',
              }))} />
          </div>
        </div>
        {harness && (
          <p className="text-fg-muted text-xs mt-3 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>
              {selected.size > 0
                ? <>{selected.size} selected × {fmtUsd2(taskBudget)} means at most{' '}
                    <span className="text-fg">
                      {fmtUsd2(Math.min(
                        (parseFloat(taskBudget) || 0) * selected.size,
                        parseFloat(runBudget) || Infinity,
                      ))}
                    </span>
                    {runBudget ? ' for the run, held down by the run budget.' : ' for the run.'}</>
                : <>Pick tasks below to see the most a run could cost.</>}
              {' '}
              {harness.streams_cost
                ? 'This harness reports cost as it goes, so a task stops the moment it reaches its cap.'
                : 'This harness only reports cost once a task finishes, so its cap is checked after the fact.'}
            </span>
          </p>
        )}
      </div>

      <div className="bg-panel border border-panel-border rounded-xl">
        <div className="flex items-start justify-between gap-4 flex-wrap p-6 pb-4">
          <div>
            <h2 className="text-fg font-semibold">Tasks</h2>
            <p className="text-fg-muted text-xs mt-1">
              <span className="text-fg">{selected.size}</span> selected
              <span className="mx-1.5 text-panel-border">·</span>
              {visible.length} of {tasks.length} shown
            </p>
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="block text-[11px] text-fg-muted mb-1">Search</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-fg-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="task or title…"
                  className="w-52 pl-8 pr-2 py-1.5 bg-field border border-field-border rounded-lg text-fg text-xs focus:border-orange" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] text-fg-muted mb-1">Language</label>
              <Select className="w-32" value={language} onChange={setLanguage} placeholder="All"
                options={[{ value: '', label: 'All' },
                  ...languages.map((l) => ({ value: l, label: l }))]} />
            </div>
            <div>
              <label className="block text-[11px] text-fg-muted mb-1" title="Tests the task must make pass">
                Scope (F2P)
              </label>
              <Select className="w-40" value={f2pBand} onChange={setF2pBand} placeholder="Any"
                options={[{ value: '', label: 'Any' },
                  ...F2P_BANDS.map((b) => ({ value: b.key, label: b.label, hint: b.hint }))]} />
            </div>
            <div>
              <label className="block text-[11px] text-fg-muted mb-1" title="Tests that must keep passing">
                Blast radius (P2P)
              </label>
              <Select className="w-40" value={p2pBand} onChange={setP2pBand} placeholder="Any"
                options={[{ value: '', label: 'Any' },
                  ...P2P_BANDS.map((b) => ({ value: b.key, label: b.label, hint: b.hint }))]} />
            </div>
            {(query || language || f2pBand || p2pBand) && (
              <button onClick={() => { setQuery(''); setLanguage(''); setF2pBand(''); setP2pBand(''); }}
                className="px-2 py-1.5 text-fg-muted hover:text-fg text-xs">Reset</button>
            )}
          </div>
        </div>

        <div className="max-h-[28rem] overflow-y-auto border-y border-panel-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-panel">
              <tr className="text-left text-[11px] uppercase tracking-wider text-fg-muted border-b border-panel-border">
                <th className="py-2.5 pl-6 pr-3 w-10">
                  <input type="checkbox" className="accent-orange"
                    checked={visible.length > 0 && visible.every((t) => selected.has(t.task_id))}
                    onChange={(e) => setSelected((prev) => {
                      const next = new Set(prev);
                      visible.forEach((t) => (e.target.checked ? next.add(t.task_id) : next.delete(t.task_id)));
                      return next;
                    })} />
                </th>
                <SortHeader label="Task" sortKey="task_id" sort={sort} onSort={sortBy}
                  className="py-2.5 pr-3" />
                <SortHeader label="Language" sortKey="language" sort={sort} onSort={sortBy}
                  className="py-2.5 pr-3 w-28" />
                <SortHeader label="F2P" sortKey="f2p_count" sort={sort} onSort={sortBy} align="right"
                  className="py-2.5 pr-3 w-20" title="Tests that must start passing" />
                <SortHeader label="P2P" sortKey="p2p_count" sort={sort} onSort={sortBy} align="right"
                  className="py-2.5 pr-6 w-24" title="Tests that must keep passing" />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const on = selected.has(t.task_id);
                return (
                  <tr key={t.task_id} onClick={() => toggle(t.task_id)}
                    className={`border-b border-panel-border/40 cursor-pointer transition-colors ${
                      on ? 'bg-orange/[0.07]' : 'hover:bg-field/40'}`}>
                    <td className="py-2.5 pl-6 pr-3">
                      <input type="checkbox" checked={on} readOnly className="accent-orange pointer-events-none" />
                    </td>
                    <td className="py-2.5 pr-3 min-w-0">
                      <div className="text-fg text-[13px] leading-tight truncate">{t.title}</div>
                      <div className="text-fg-muted text-[11px] font-mono truncate mt-0.5">{t.task_id}</div>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        LANG_STYLE[t.language] || 'bg-fg/10 text-fg-muted'}`}>
                        {t.language}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-right text-fg text-xs tabular-nums">{fmtInt(t.f2p_count)}</td>
                    <td className="py-2.5 pr-6 text-right text-fg-muted text-xs tabular-nums">{fmtInt(t.p2p_count)}</td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-fg-muted text-xs">
                  No tasks match these filters
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 pb-6 pt-4">
        <div className="mt-4 flex items-center gap-3">
          <button onClick={launch} disabled={busy || !selected.size || !kind || !model}
            title={!model ? 'Pick a model first' : undefined}
            className="flex items-center gap-1.5 px-4 py-2 bg-orange hover:bg-orange-hover text-fg rounded text-sm disabled:opacity-50">
            <Play className="w-4 h-4" />{busy ? 'Starting…' : `Run ${selected.size || ''} task${selected.size === 1 ? '' : 's'}`}
          </button>
          <span className="text-fg-muted text-xs">Runs a few tasks at a time.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Run detail ───────────────────────────────────────────────────────────────

function RunDetail({ runId, onBack, onMessage }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await getEvalRun(runId); setData(res.data); }
    catch (e) { onMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to load run' }); }
  }, [runId, onMessage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.run?.is_active) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [data?.run?.is_active, load]);

  if (!data) return <div className="text-center text-fg-muted py-16">Loading…</div>;
  const { run, results } = data;
  const t = run.totals || {};
  const live = liveTotals(run);
  const tok = { ...(t.tokens || {}) };
  for (const k of ['total', 'input', 'output', 'reasoning', 'cache_read', 'cache_write']) {
    tok[k] = (tok[k] || 0) + (live.tokens[k] || 0);
  }
  // Unpriced means nobody knows, which is not the same as free — $0.00 would
  // read as a measurement. The label says so instead.
  const sources = [...(results || []).map((r) => r.cost_source), ...live.sources].filter(Boolean);
  const unpriced = sources.length > 0 && sources.every((s) => s === 'unpriced');
  const estimated = sources.includes('estimated');

  const cancel = async () => {
    setBusy(true);
    try { await cancelEvalRun(runId); onMessage({ type: 'success', text: 'Cancellation requested' }); load(); }
    catch (e) { onMessage({ type: 'error', text: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-fg-muted hover:text-fg mb-4">
        <ChevronLeft className="w-4 h-4" /> All runs
      </button>

      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold text-fg font-mono">{run.run_id}</h2>
            <StatusPill status={run.status} />
          </div>
          <p className="text-fg-muted text-xs mt-1">
            {harnessLabel(run.harness || {})} · <span className="font-mono">{run.harness?.model}</span>
            {run.harness?.provider && <> · via <span className="font-mono">{run.harness.provider}</span></>}
            {' · '}started {fmtTime(run.started_at)}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {run.is_active && (
            <button onClick={cancel} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-panel border border-panel-border text-fg-muted hover:text-red-400 rounded text-sm disabled:opacity-50">
              <Ban className="w-4 h-4" />Cancel
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat label="Accuracy" value={fmtPct(t.accuracy)} hint={`${t.resolved || 0} of ${run.completed_tasks || 0} resolved`} />
        <Stat label={estimated && !unpriced ? 'Cost (est.)' : 'Cost'}
          value={unpriced ? '—' : fmtUsd((t.cost_usd || 0) + live.cost)}
          hint={unpriced ? 'No pricing available for this model'
            : `${fmtUsd(run.task_budget_usd ?? run.budget_per_task_usd)}/task${run.run_budget_usd ? ` · ${fmtUsd2(run.run_budget_usd)}/run` : ''} cap`} />
        {/* Reasoning only when a model reports it separately — claude-code counts
            thinking inside output, so a permanent "0 reasoning" would mislead. */}
        {/* `input` counts only what reached the model uncached, so in an agentic
            loop it is ~0 from the second turn on and reads as though nothing was
            sent. The prompt still went — as a cache read — so the total is shown
            with the cached share named. */}
        <Stat label="Tokens" value={fmtInt(tok.total)}
          hint={`${fmtInt(tok.input + tok.cache_read + tok.cache_write)} in`
            + (tok.cache_read ? ` (${fmtInt(tok.cache_read)} cached)` : '')
            + ` · ${fmtInt(tok.output)} out`
            + (tok.reasoning ? ` · ${fmtInt(tok.reasoning)} reasoning` : '')} />
        <Stat label="Cache read" value={fmtInt(tok.cache_read)} hint={`${fmtInt(tok.cache_write)} written`} />
        {/* Agent time only accrues as tasks land, so it reads 0s for the whole of
            the first trial. While the run is live, elapsed is the honest number. */}
        <Stat label="Tool calls" value={fmtInt((t.tool_calls || 0) + live.toolCalls)}
          hint={run.is_active ? `${fmtDur(secondsSince(run.started_at))} elapsed` : fmtDur(t.duration_sec)} />
      </div>

      <div className="mb-6">
        <RunProgress run={run} results={results} />
      </div>

      <div className="bg-panel border border-panel-border rounded-xl p-6">
        <h3 className="text-fg font-semibold mb-3">Per-task results</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="text-left text-xs text-fg-muted border-b border-panel-border">
                {['Task', 'Result', 'F2P', 'P2P', 'Cost', 'Tokens', 'Tools', 'Time', 'Source'].map((c) => (
                  <th key={c} className="py-2 pr-3 font-medium">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.task_id} className="border-b border-panel-border/50">
                  <td className="py-2 pr-3 font-mono text-xs text-fg">{r.task_id}</td>
                  <td className="py-2 pr-3">
                    <Outcome kind={outcomeOf(r)} title={r.error || ''} />
                  </td>
                  <td className="py-2 pr-3 text-xs tabular-nums">
                    {r.tests?.f2p_total
                      ? <span className={r.tests.f2p_passed === r.tests.f2p_total ? 'text-green-400' : 'text-fg-muted'}>
                          {r.tests.f2p_passed}/{r.tests.f2p_total}
                        </span>
                      : <span className="text-fg-muted">—</span>}
                  </td>
                  <td className="py-2 pr-3 text-xs tabular-nums">
                    {r.tests?.p2p_total
                      ? <span className={r.tests.p2p_passed === r.tests.p2p_total ? 'text-green-400' : 'text-red-400'}>
                          {fmtInt(r.tests.p2p_passed)}/{fmtInt(r.tests.p2p_total)}
                        </span>
                      : <span className="text-fg-muted">—</span>}
                  </td>
                  <td className="py-2 pr-3 text-fg-muted text-xs">{fmtUsd(r.cost_usd)}</td>
                  <td className="py-2 pr-3 text-fg-muted text-xs">{fmtInt(r.tokens?.total)}</td>
                  <td className="py-2 pr-3 text-fg-muted text-xs">{fmtInt(r.total_tool_calls)}</td>
                  <td className="py-2 pr-3 text-fg-muted text-xs">{fmtDur(r.duration_sec)}</td>
                  <td className="py-2 pr-3 text-fg-muted text-xs font-mono">
                    {r.tests?.apply_failed ? 'patch rejected'
                      : r.timed_out ? 'timed out'
                      : r.budget_stopped ? 'budget' : r.reward_source}
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-fg-muted text-xs">
                  {run.is_active ? 'First task in progress…' : 'No results'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── History tab ──────────────────────────────────────────────────────────────

function HistoryTab({ runs, total, page, setPage, onOpen }) {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-fg font-semibold">Run history</h2>
          <p className="text-fg-muted text-xs mt-1">
            Every run keeps the harness version it executed with, so past results stay comparable.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="text-left text-xs text-fg-muted border-b border-panel-border">
              {['Run', 'Harness', 'Version', 'Model', 'Tasks', 'Accuracy', 'Cost', 'Tokens', 'Status', 'Started'].map((c) => (
                <th key={c} className="py-2 pr-3 font-medium">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const t = r.totals || {};
              // `totals` only moves when a task lands, so a running row read $0
              // and 0 tokens while its detail page showed the work in progress.
              // Same fold as there, so the two agree.
              const live = liveTotals(r);
              const cost = (t.cost_usd || 0) + live.cost;
              const tokens = (t.tokens?.total || 0) + (live.tokens.total || 0);
              const estimated = live.sources.includes('estimated')
                || (r.results || []).some((x) => x.cost_source === 'estimated');
              return (
                <tr key={r.run_id} onClick={() => onOpen(r.run_id)}
                  className="border-b border-panel-border/50 cursor-pointer hover:bg-field/50">
                  <td className="py-2 pr-3 font-mono text-xs text-fg">{r.run_id}</td>
                  <td className="py-2 pr-3 text-fg text-xs">{r.harness?.name}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-fg-muted">{r.harness?.version || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-fg-muted max-w-[200px] truncate" title={r.harness?.model}>{r.harness?.model}</td>
                  <td className="py-2 pr-3 text-fg-muted text-xs">{r.completed_tasks || 0}/{t.tasks || 0}</td>
                  <td className="py-2 pr-3 text-fg text-xs">{fmtPct(t.accuracy)}</td>
                  <td className="py-2 pr-3 text-fg-muted text-xs whitespace-nowrap">
                    {fmtUsd(cost)}
                    {estimated && <span className="text-orange/80 text-[10px] ml-1">est.</span>}
                  </td>
                  <td className="py-2 pr-3 text-fg-muted text-xs">{fmtInt(tokens)}</td>
                  <td className="py-2 pr-3">
                    <span className="flex items-center gap-1.5">
                      <StatusPill status={r.status} />
                      {r.is_active && <Loader2 className="w-3 h-3 text-orange animate-spin" />}
                      {r.is_active && (
                        <span className="text-[11px] text-fg-muted tabular-nums">
                          {r.completed_tasks || 0}/{(r.totals || {}).tasks || 0}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-fg-muted text-xs whitespace-nowrap">{fmtTime(r.created_at)}</td>
                </tr>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan={10} className="py-6 text-center text-fg-muted text-xs">No runs yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 25 && (
        <div className="flex items-center justify-end gap-2 mt-4 text-xs">
          <span className="text-fg-muted">{page * 25 + 1}–{Math.min((page + 1) * 25, total)} of {total}</span>
          <button disabled={page === 0} onClick={() => setPage(page - 1)}
            className="p-1.5 text-fg-muted hover:text-fg disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <button disabled={(page + 1) * 25 >= total} onClick={() => setPage(page + 1)}
            className="p-1.5 text-fg-muted hover:text-fg disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'run', label: 'New run', icon: Play },
  { key: 'history', label: 'History', icon: Clock },
  { key: 'harnesses', label: 'Harnesses', icon: Server },
];

export default function HarnessEval() {
  const [tab, setTab] = useState('run');
  const [harnesses, setHarnesses] = useState([]);
  const [effortLevels, setEffortLevels] = useState([]);
  const [defaultEffort, setDefaultEffort] = useState('medium');
  const [providers, setProviders] = useState([]);
  const [suggested, setSuggested] = useState({});
  const [models, setModels] = useState({ by_harness: {}, fallback: {} });
  const [tasks, setTasks] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [env, setEnv] = useState({ docker_available: true, corpus_ok: true });
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [openRun, setOpenRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const notify = useCallback((m) => {
    setMessage(m);
    if (m.type === 'success') setTimeout(() => setMessage(null), 4000);
  }, []);

  const loadHarnesses = useCallback(async () => {
    const res = await getEvalHarnesses();
    setHarnesses(res.data.harnesses || []);
    setEffortLevels(res.data.effort_levels || []);
    setDefaultEffort(res.data.default_effort || 'medium');
  }, []);

  const loadRuns = useCallback(async () => {
    const res = await getEvalRuns(25, page * 25);
    setRuns(res.data.runs || []);
    setTotal(res.data.total || 0);
  }, [page]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [tasksRes, envRes, provRes, modelRes] = await Promise.all([
          // The corpus is fetched, not vendored, so "no tasks" on a fresh checkout
          // is a setup step rather than a failure. Pass the server's instruction
          // through instead of rendering an empty benchmark with no explanation.
          getEvalTasks().catch((e) => ({
            data: { tasks: [], languages: [], error: e.response?.data?.detail || 'Task corpus unavailable' },
          })),
          getEvalEnvironment(),
          getEvalProviders().catch(() => ({ data: { providers: [], auth_modes: [], suggested: {} } })),
          getEvalModels().catch(() => ({ data: { by_harness: {}, fallback: {} } })),
        ]);
        if (!alive) return;
        setTasks(tasksRes.data.tasks || []);
        setLanguages(tasksRes.data.languages || []);
        if (tasksRes.data.error) notify({ type: 'error', text: tasksRes.data.error });
        setEnv(envRes.data);
        setProviders(provRes.data.providers || []);
        setSuggested(provRes.data.suggested || {});
        setModels(modelRes.data || { by_harness: {}, fallback: {} });
        await Promise.all([loadHarnesses(), loadRuns()]);
      } catch (e) {
        if (alive) notify({ type: 'error', text: 'Failed to load Harness Eval' });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [loadHarnesses, loadRuns, notify]);

  // Keep the history list live while anything is still running.
  useEffect(() => {
    if (openRun || !runs.some((r) => r.is_active)) return undefined;
    const id = setInterval(loadRuns, POLL_MS);
    return () => clearInterval(id);
  }, [runs, openRun, loadRuns]);

  const refresh = () => { loadHarnesses(); loadRuns(); };

  const Header = (
    <div className="flex justify-between items-start mb-6">
      <div>
        <h1 className="text-2xl font-bold text-fg">Harness Eval</h1>
        <p className="text-fg-muted text-sm mt-1">
          Benchmark coding-agent harnesses on identical tasks and compare accuracy, cost and tokens.
          {env.task_count ? ` ${env.task_count} tasks available.` : ''}
        </p>
      </div>
      <button onClick={refresh}
        className="flex items-center gap-2 px-3 py-2 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg text-sm">
        <RefreshCw className="w-4 h-4" />Refresh
      </button>
    </div>
  );

  const Message = message && (
    <div className={`mb-4 p-3 rounded-lg text-sm flex justify-between ${
      message.type === 'success'
        ? 'bg-green-500/10 border border-green-500/20 text-green-400'
        : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
      <span>{message.text}</span>
      <button onClick={() => setMessage(null)} className="opacity-70 hover:opacity-100">✕</button>
    </div>
  );

  if (loading) return <div>{Header}<div className="text-center text-fg-muted py-16">Loading…</div></div>;

  if (openRun) {
    return (
      <div>
        {Header}{Message}
        <RunDetail runId={openRun} onBack={() => { setOpenRun(null); loadRuns(); }} onMessage={notify} />
      </div>
    );
  }

  return (
    <div>
      {Header}{Message}

      <div className="flex items-center gap-1 mb-5 border-b border-panel-border">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-orange text-orange'
                : 'border-transparent text-fg-muted hover:text-fg'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === 'run' && (
        <NewRunTab harnesses={harnesses} models={models} tasks={tasks} languages={languages} env={env}
          onLaunched={(id) => { loadRuns(); setOpenRun(id); }} onMessage={notify} />
      )}
      {tab === 'history' && (
        <HistoryTab runs={runs} total={total} page={page} setPage={setPage} onOpen={setOpenRun} />
      )}
      {tab === 'harnesses' && (
        <HarnessesTab harnesses={harnesses} providers={providers}
          suggested={suggested} models={models} effortLevels={effortLevels}
          defaultEffort={defaultEffort} onChanged={loadHarnesses} onMessage={notify} />
      )}
    </div>
  );
}
